import { useCallback, useEffect, useRef, useState } from 'react';
import type { WheelDateValue } from '../components/WheelDatePicker';
import {
  ensureWorkDustHistoryEmployee,
  entryFromApi,
  fetchWorkDustHistoryProgress,
  saveWorkHistoryEntry,
  upsertWorkDustHistoryProgress,
} from './careerHistorySurveyApi';
import {
  applyPneuFromProgress,
  clearPneuDraftFromStep,
  committedPneuStepsForDraft,
  isPneuStep,
  nextPneuStepAfter,
  persistPneuProgress,
  PNEU_FIELD_STEPS,
  PNEU_GATE_STEP,
  PNEU_SECTION_COMPLETE_STEP,
  prevPneuStepForUndo,
  userAnswerForPneuStep,
  type PneuCheckupStep,
} from './careerHistorySurveyPneuHelpers';
import {
  buildResumeStep,
  committedStepsForDraft,
  isEntryStep,
  isNewEntryAfterRecordGateYes,
  lastFieldStepBeforeSummary,
  nextStepAfter,
  prevStepForUndo,
  stepQuestionKey,
  validateEndAfterStart,
  WORK_HISTORY_REVIEW_STEP,
} from './careerHistorySurveySteps';
import type { WorkHistoryEntryStep } from './careerHistorySurveyTypes';
import { buildWorkHistoryEntrySummary, userAnswerForEntryStep } from './careerHistorySurveySummary';
import { withAutoDustDuration } from './dustDurationFormat';
import type {
  CareerHistorySurveyStep,
  CompletedWorkHistoryEntry,
  PneuCheckupDraft,
  WorkHistoryEntryDraft,
  YesNo,
} from './careerHistorySurveyTypes';
import { emptyPneuCheckupDraft, emptyPneuSlotDraft, emptyWorkHistoryEntryDraft, WORK_HISTORY_ENTRY_STEPS as ENTRY_STEPS } from './careerHistorySurveyTypes';

function defaultDateWheel(): WheelDateValue {
  const today = new Date();
  return { year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() };
}

function wheelFromIso(iso: string, fallback: WheelDateValue): WheelDateValue {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return fallback;
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

function isoFromWheel(w: WheelDateValue): string {
  return `${w.year}-${String(w.month).padStart(2, '0')}-${String(w.day).padStart(2, '0')}`;
}

export function useCareerHistorySurveyFlow(
  currentEmail: string | null | undefined,
  invitationId: string | null | undefined,
  t: (key: string) => string,
  locale: string
) {
  const [isLoading, setIsLoading] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [step, setStep] = useState<CareerHistorySurveyStep>('workHistoryIntro');
  const [draft, setDraft] = useState<WorkHistoryEntryDraft>(emptyWorkHistoryEntryDraft());
  const [completed, setCompleted] = useState<CompletedWorkHistoryEntry[]>([]);
  const [committed, setCommitted] = useState<Set<CareerHistorySurveyStep>>(() => new Set());
  const [recordGateAnswer, setRecordGateAnswer] = useState<YesNo | ''>('');
  const [recordGateCommitted, setRecordGateCommitted] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editReturnStep, setEditReturnStep] = useState<CareerHistorySurveyStep | null>(null);
  /** Saved entry index awaiting record-gate answer; summary may be collapsed. */
  const [pendingGateEntryIndex, setPendingGateEntryIndex] = useState<number | null>(null);
  /** false = summary bubble only; true = per-field Q&A visible for pending entry re-edit. */
  const [entryFieldsExpanded, setEntryFieldsExpanded] = useState(false);
  const [startDateWheel, setStartDateWheel] = useState<WheelDateValue>(defaultDateWheel);
  const [endDateWheel, setEndDateWheel] = useState<WheelDateValue>(defaultDateWheel);
  const [sectionWorkHistoryDone, setSectionWorkHistoryDone] = useState(false);
  const [sectionPneuDone, setSectionPneuDone] = useState(false);
  const [pneuDraft, setPneuDraft] = useState<PneuCheckupDraft>(emptyPneuCheckupDraft());
  const [pneuCommitted, setPneuCommitted] = useState<Set<PneuCheckupStep>>(() => new Set());
  const [pneuGateCommitted, setPneuGateCommitted] = useState(false);
  const [pneu1DateWheel, setPneu1DateWheel] = useState<WheelDateValue>(defaultDateWheel);
  const [pneu2DateWheel, setPneu2DateWheel] = useState<WheelDateValue>(defaultDateWheel);
  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const currentQuestionRef = useRef<HTMLDivElement | null>(null);

  const reviewMode = step === WORK_HISTORY_REVIEW_STEP;
  const pneuSectionActive = sectionWorkHistoryDone && !sectionPneuDone;
  const inPneuFlow = pneuSectionActive && isPneuStep(step) && editIndex === null;
  const pneuUndoActive =
    sectionWorkHistoryDone &&
    editIndex === null &&
    isPneuStep(step) &&
    (!sectionPneuDone || step === PNEU_SECTION_COMPLETE_STEP);
  const pendingReEdit = pendingGateEntryIndex !== null && entryFieldsExpanded;
  const inMemberFlow =
    editIndex === null &&
    (pendingReEdit ||
      isEntryStep(step) ||
      (step === 'recordGate' && recordGateAnswer === 'yes' && recordGateCommitted));
  const editingFromReview = editIndex !== null;
  const showFieldEdit = editIndex !== null && step === WORK_HISTORY_REVIEW_STEP;
  const newEntryAfterRecordGateYes = isNewEntryAfterRecordGateYes({
    editIndex,
    pendingReEdit,
    recordGateCommitted,
    recordGateAnswer,
  });
  const showPendingSummaryUndo =
    pendingGateEntryIndex !== null &&
    !entryFieldsExpanded &&
    step === 'recordGate' &&
    !recordGateCommitted;

  const showUndoRecordGateNo =
    recordGateCommitted &&
    recordGateAnswer === 'no' &&
    sectionWorkHistoryDone &&
    !pneuGateCommitted &&
    editIndex === null;

  const showUndoPneuGate =
    pneuGateCommitted &&
    editIndex === null &&
    (step === 'pneu1Date' ||
      (step === PNEU_SECTION_COMPLETE_STEP && pneuDraft.gateAnswer === 'no'));

  const markPneuCommitted = useCallback((s: PneuCheckupStep) => {
    setPneuCommitted((prev) => new Set(prev).add(s));
  }, []);

  const isPneuCommitted = useCallback((s: PneuCheckupStep) => pneuCommitted.has(s), [pneuCommitted]);

  const markCommitted = useCallback((s: CareerHistorySurveyStep) => {
    setCommitted((prev) => new Set(prev).add(s));
  }, []);

  const isCommitted = useCallback((s: CareerHistorySurveyStep) => committed.has(s), [committed]);

  const resetMemberState = useCallback(() => {
    setDraft(emptyWorkHistoryEntryDraft());
    setCommitted(new Set());
    setStartDateWheel(defaultDateWheel());
    setEndDateWheel(defaultDateWheel());
  }, []);

  const applyProgressEntries = useCallback(
    (entries: ReturnType<typeof entryFromApi>[]) => {
      setCompleted(
        entries.map((snap) => ({
          snapshot: snap,
          text: buildWorkHistoryEntrySummary(snap, t, locale),
        }))
      );
    },
    [t, locale]
  );

  const persistPneu = useCallback(
    async (draftToSave: PneuCheckupDraft, nextStep: CareerHistorySurveyStep, sectionDone?: boolean) => {
      if (!currentEmail?.trim()) return false;
      const saved = await persistPneuProgress({
        email: currentEmail.trim(),
        invitationId: invitationId ?? undefined,
        draft: draftToSave,
        nextStep,
        sectionPneuDone: sectionDone,
      });
      if (!saved) {
        setSaveError(t('submitDocuments.careerHistorySurvey.saveFailed'));
        return false;
      }
      setSaveError(null);
      const applied = applyPneuFromProgress(saved);
      if (nextStep === PNEU_GATE_STEP && !draftToSave.gateAnswer) {
        setPneuDraft(draftToSave);
        setPneuCommitted(new Set());
        setPneuGateCommitted(false);
        setSectionPneuDone(false);
      } else {
        setPneuDraft(applied.draft);
        setPneuCommitted(applied.committed);
        setPneuGateCommitted(applied.gateCommitted);
        setSectionPneuDone(saved.sectionPneuDone === true);
      }
      return true;
    },
    [currentEmail, invitationId, t]
  );

  const persistStep = useCallback(
    async (nextStep: CareerHistorySurveyStep, sectionDone?: boolean) => {
      if (!currentEmail?.trim()) return false;
      const saved = await upsertWorkDustHistoryProgress({
        email: currentEmail.trim(),
        invitationId: invitationId ?? undefined,
        currentStepId: nextStep,
        sectionWorkHistoryDone: sectionDone,
      });
      if (!saved) {
        setSaveError(t('submitDocuments.careerHistorySurvey.saveFailed'));
        return false;
      }
      setSaveError(null);
      if (saved.entries) applyProgressEntries(saved.entries.map(entryFromApi));
      return true;
    },
    [currentEmail, invitationId, t, applyProgressEntries]
  );

  const beginPneuSection = useCallback(async () => {
    setSectionWorkHistoryDone(true);
    setPendingGateEntryIndex(null);
    setEntryFieldsExpanded(false);
    setStep(PNEU_GATE_STEP);
    await persistStep(PNEU_GATE_STEP, true);
  }, [persistStep]);

  const advancePneuFrom = useCallback(
    async (from: PneuCheckupStep, nextDraft: PneuCheckupDraft) => {
      markPneuCommitted(from);
      const next = nextPneuStepAfter(from, nextDraft.gateAnswer);
      if (!next) return;
      const sectionDone = next === PNEU_SECTION_COMPLETE_STEP;
      if (sectionDone) setSectionPneuDone(true);
      setStep(next);
      await persistPneu(nextDraft, next, sectionDone);
    },
    [markPneuCommitted, persistPneu]
  );

  useEffect(() => {
    if (!currentEmail?.trim()) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setSaveError(null);
      await ensureWorkDustHistoryEmployee({
        email: currentEmail.trim(),
        invitationId: invitationId ?? undefined,
      });
      const progress = await fetchWorkDustHistoryProgress(
        currentEmail.trim(),
        invitationId ?? undefined
      );
      if (cancelled) return;
      const entries = (progress?.entries ?? []).map(entryFromApi);
      applyProgressEntries(entries);
      const sectionDone = progress?.sectionWorkHistoryDone === true;
      const pneuDone = progress?.sectionPneuDone === true;
      const pneuApplied = applyPneuFromProgress(progress);
      setSectionWorkHistoryDone(sectionDone);
      setSectionPneuDone(pneuDone);
      setPneuDraft(pneuApplied.draft);
      setPneuCommitted(pneuApplied.committed);
      setPneuGateCommitted(pneuApplied.gateCommitted);
      const resume = buildResumeStep(
        sectionDone,
        pneuDone,
        entries.length,
        progress?.currentStepId,
        progress?.pneuCheckup
      );
      setStep(resume);
      if (sectionDone) {
        setRecordGateAnswer('no');
        setRecordGateCommitted(true);
        setPendingGateEntryIndex(null);
        setEntryFieldsExpanded(false);
      } else if (resume === 'recordGate') {
        setRecordGateAnswer('');
        setRecordGateCommitted(false);
        setPendingGateEntryIndex(entries.length > 0 ? entries.length - 1 : null);
        setEntryFieldsExpanded(false);
      } else if (isEntryStep(resume)) {
        setRecordGateAnswer('yes');
        setRecordGateCommitted(true);
        setPendingGateEntryIndex(null);
        setEntryFieldsExpanded(true);
        resetMemberState();
      } else {
        setRecordGateAnswer('');
        setRecordGateCommitted(false);
        setPendingGateEntryIndex(null);
        setEntryFieldsExpanded(false);
      }
      setEditIndex(null);
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [currentEmail, invitationId, applyProgressEntries, resetMemberState]);

  useEffect(() => {
    if (step === 'startDate') setStartDateWheel(wheelFromIso(draft.startDate, defaultDateWheel()));
  }, [draft.startDate, step]);

  useEffect(() => {
    if (step === 'endDate') setEndDateWheel(wheelFromIso(draft.endDate, defaultDateWheel()));
  }, [draft.endDate, step]);

  useEffect(() => {
    if (step === 'pneu1Date') {
      setPneu1DateWheel(wheelFromIso(pneuDraft.slot1.checkupDate, defaultDateWheel()));
    }
  }, [pneuDraft.slot1.checkupDate, step]);

  useEffect(() => {
    if (step === 'pneu2Date') {
      setPneu2DateWheel(wheelFromIso(pneuDraft.slot2.checkupDate, defaultDateWheel()));
    }
  }, [pneuDraft.slot2.checkupDate, step]);

  const skipIntro = useCallback(async () => {
    markCommitted('workHistoryIntro');
    setStep('recordGate');
    await persistStep('recordGate');
  }, [markCommitted, persistStep]);

  const advanceFrom = useCallback(
    async (from: CareerHistorySurveyStep, answerText?: string) => {
      markCommitted(from);
      const next = nextStepAfter(from, recordGateAnswer);
      if (!next) return;
      if (from === 'entrySummary') {
        if (!currentEmail?.trim()) return;
        const entryDraft = withAutoDustDuration(draft, locale);
        setDraft(entryDraft);
        markCommitted('dustDuration');
        const saved = await saveWorkHistoryEntry({
          email: currentEmail.trim(),
          invitationId: invitationId ?? undefined,
          draft: entryDraft,
          currentStepId: next,
          locale,
        });
        if (!saved) {
          setSaveError(t('submitDocuments.careerHistorySurvey.saveFailed'));
          return;
        }
        setSaveError(null);
        const entries = (saved.entries ?? []).map(entryFromApi);
        applyProgressEntries(entries);
        resetMemberState();
        setPendingGateEntryIndex(Math.max(0, entries.length - 1));
        setEntryFieldsExpanded(false);
        setRecordGateAnswer('');
        setRecordGateCommitted(false);
        setStep(next);
        await persistStep(next);
        return;
      }
      if (from === 'recordGate' && recordGateAnswer === 'no') {
        setPendingGateEntryIndex(null);
        setEntryFieldsExpanded(false);
        await beginPneuSection();
        return;
      }
      setStep(next);
      await persistStep(next);
      void answerText;
    },
    [
      markCommitted,
      recordGateAnswer,
      currentEmail,
      invitationId,
      draft,
      t,
      applyProgressEntries,
      resetMemberState,
      persistStep,
      locale,
      beginPneuSection,
    ]
  );

  const handleRecordGate = useCallback(
    async (v: YesNo) => {
      setRecordGateAnswer(v);
      setRecordGateCommitted(true);
      setPendingGateEntryIndex(null);
      setEntryFieldsExpanded(false);
      if (v === 'yes') {
        resetMemberState();
        markCommitted('recordGate');
        setStep('siteName');
        await persistStep('siteName');
        return;
      }
      markCommitted('recordGate');
      await beginPneuSection();
    },
    [markCommitted, beginPneuSection, resetMemberState, persistStep]
  );

  const handleUndoPendingSummary = useCallback(async () => {
    if (pendingGateEntryIndex === null) return;
    const entry = completed[pendingGateEntryIndex];
    if (!entry) return;
    const snapshot = { ...entry.snapshot };
    setDraft(snapshot);
    const comm = committedStepsForDraft(snapshot);
    comm.delete('entrySummary');
    setCommitted(comm);
    setEntryFieldsExpanded(true);
    const target = lastFieldStepBeforeSummary(snapshot);
    setStep(target);
    await persistStep(target);
  }, [pendingGateEntryIndex, completed, persistStep]);

  const handleUndoRecordGateNo = useCallback(async () => {
    setRecordGateCommitted(false);
    setRecordGateAnswer('');
    setSectionWorkHistoryDone(false);
    setSectionPneuDone(false);
    setPneuDraft(emptyPneuCheckupDraft());
    setPneuCommitted(new Set());
    setPneuGateCommitted(false);
    setCommitted((prev) => {
      const next = new Set(prev);
      next.delete('recordGate');
      return next;
    });
    if (completed.length > 0) {
      setPendingGateEntryIndex(completed.length - 1);
      setEntryFieldsExpanded(false);
    } else {
      setPendingGateEntryIndex(null);
      setEntryFieldsExpanded(false);
    }
    setStep('recordGate');
    await persistStep('recordGate', false);
  }, [completed.length, persistStep]);

  const returnToRecordGateAfterYes = useCallback(async () => {
    setDraft(emptyWorkHistoryEntryDraft());
    setStartDateWheel(defaultDateWheel());
    setEndDateWheel(defaultDateWheel());
    setCommitted(new Set());
    setRecordGateCommitted(false);
    setRecordGateAnswer('');
    if (completed.length > 0) {
      setPendingGateEntryIndex(completed.length - 1);
      setEntryFieldsExpanded(false);
    } else {
      setPendingGateEntryIndex(null);
      setEntryFieldsExpanded(false);
    }
    setStep('recordGate');
    await persistStep('recordGate', false);
  }, [completed.length, persistStep]);

  const maybeReturnAfterEntryEdit = useCallback(
    async (entryDraft: WorkHistoryEntryDraft): Promise<boolean> => {
      if (editReturnStep === null || editIndex === null) return false;
      if (!currentEmail?.trim()) return false;

      const idx = editIndex;
      const returnStep = editReturnStep;
      const snapshot = { ...entryDraft };
      const saved = await saveWorkHistoryEntry({
        email: currentEmail.trim(),
        invitationId: invitationId ?? undefined,
        draft: snapshot,
        currentStepId: returnStep,
        locale,
      });
      if (!saved) {
        setSaveError(t('submitDocuments.careerHistorySurvey.saveFailed'));
        return true;
      }
      setSaveError(null);
      applyProgressEntries((saved.entries ?? []).map(entryFromApi));
      const text = buildWorkHistoryEntrySummary(snapshot, t, locale);
      setCompleted((prev) => {
        const next = [...prev];
        if (idx >= next.length) return prev;
        next[idx] = { text, snapshot };
        return next;
      });
      setDraft(snapshot);
      setCommitted(committedStepsForDraft(snapshot));
      setStep(returnStep);
      await persistStep(returnStep, sectionWorkHistoryDone);
      return true;
    },
    [
      editReturnStep,
      editIndex,
      currentEmail,
      invitationId,
      locale,
      t,
      applyProgressEntries,
      persistStep,
      sectionWorkHistoryDone,
    ]
  );

  const submitTextField = useCallback(
    async (field: keyof WorkHistoryEntryDraft, value: string, from: CareerHistorySurveyStep) => {
      const nextDraft = { ...draft, [field]: value };
      setDraft(nextDraft);
      markCommitted(from);
      if (await maybeReturnAfterEntryEdit(nextDraft)) return;
      const next = nextStepAfter(from, 'yes');
      if (!next) return;
      setStep(next);
      await persistStep(next);
    },
    [draft, markCommitted, persistStep, maybeReturnAfterEntryEdit]
  );

  const handleStartDate = useCallback(async () => {
    const iso = isoFromWheel(startDateWheel);
    let nextDraft = { ...draft, startDate: iso };
    // When editing from review, keep dust duration in sync with dates.
    if (editReturnStep !== null && editIndex !== null) {
      nextDraft = withAutoDustDuration(nextDraft, locale);
    }
    setDraft(nextDraft);
    markCommitted('startDate');
    if (await maybeReturnAfterEntryEdit(nextDraft)) return;
    setStep('endDate');
    await persistStep('endDate');
  }, [
    startDateWheel,
    draft,
    markCommitted,
    persistStep,
    maybeReturnAfterEntryEdit,
    editReturnStep,
    editIndex,
    locale,
  ]);

  const handleEndDate = useCallback(async () => {
    const iso = isoFromWheel(endDateWheel);
    let nextDraft = withAutoDustDuration(
      { ...draft, endDate: iso, endDateSkipped: false },
      locale
    );
    const err = validateEndAfterStart(nextDraft);
    if (err) {
      setSaveError(t('submitDocuments.careerHistorySurvey.validation.endBeforeStart'));
      return;
    }
    setSaveError(null);
    setDraft(nextDraft);
    markCommitted('endDate');
    markCommitted('dustDuration');
    if (await maybeReturnAfterEntryEdit(nextDraft)) return;
    setStep('entrySummary');
    await persistStep('entrySummary');
  }, [endDateWheel, draft, markCommitted, persistStep, t, maybeReturnAfterEntryEdit, locale]);

  const skipEndDate = useCallback(async () => {
    const nextDraft = withAutoDustDuration(
      { ...draft, endDate: '', endDateSkipped: true },
      locale
    );
    setDraft(nextDraft);
    markCommitted('endDate');
    markCommitted('dustDuration');
    if (await maybeReturnAfterEntryEdit(nextDraft)) return;
    setStep('entrySummary');
    await persistStep('entrySummary');
  }, [draft, markCommitted, persistStep, maybeReturnAfterEntryEdit, locale]);

  const confirmEntrySummary = useCallback(async () => {
    markCommitted('entrySummary');
    await advanceFrom('entrySummary');
  }, [markCommitted, advanceFrom]);

  const handleUndoStep = useCallback(
    async (undoFrom: CareerHistorySurveyStep) => {
      if (undoFrom === 'siteName' && newEntryAfterRecordGateYes) {
        await returnToRecordGateAfterYes();
        return;
      }

      if (undoFrom === 'entrySummary') {
        const target = lastFieldStepBeforeSummary(draft);
        setCommitted((prev) => {
          const next = new Set(prev);
          next.delete('entrySummary');
          return next;
        });
        setStep(target);
        await persistStep(target);
        return;
      }

      if (!isEntryStep(undoFrom)) return;

      const fieldStep = undoFrom as WorkHistoryEntryStep;
      const fromIdx = ENTRY_STEPS.indexOf(fieldStep);
      setCommitted((prev) => {
        const next = new Set(prev);
        for (let i = fromIdx + 1; i < ENTRY_STEPS.length; i++) {
          next.delete(ENTRY_STEPS[i]);
        }
        return next;
      });
      setStep(fieldStep);
      await persistStep(fieldStep);
    },
    [
      newEntryAfterRecordGateYes,
      draft,
      persistStep,
      returnToRecordGateAfterYes,
    ]
  );

  const handleEditSummary = useCallback(
    (index: number) => {
      const entry = completed[index];
      if (!entry) return;
      setPendingGateEntryIndex(null);
      setEntryFieldsExpanded(false);
      setEditIndex(index);
      setEditReturnStep(sectionWorkHistoryDone ? step : WORK_HISTORY_REVIEW_STEP);
      setDraft({ ...entry.snapshot });
      setCommitted(committedStepsForDraft(entry.snapshot));
      setStep(WORK_HISTORY_REVIEW_STEP);
    },
    [completed, sectionWorkHistoryDone, step]
  );

  const beginEditField = useCallback(
    (fieldStep: WorkHistoryEntryStep) => {
      if (editIndex === null) return;
      // Duration is derived from dates — not editable as its own step.
      if (fieldStep === 'dustDuration') return;
      if (fieldStep === 'startDate') {
        setStartDateWheel(wheelFromIso(draft.startDate, defaultDateWheel()));
      }
      if (fieldStep === 'endDate') {
        setEndDateWheel(wheelFromIso(draft.endDate, defaultDateWheel()));
      }
      setStep(fieldStep);
    },
    [editIndex, draft]
  );

  const collapseEditReview = useCallback(() => {
    const returnStep = editReturnStep ?? WORK_HISTORY_REVIEW_STEP;
    setEditIndex(null);
    setEditReturnStep(null);
    resetMemberState();
    setStep(returnStep);
  }, [resetMemberState, editReturnStep]);

  const showEditOnSummary = useCallback(
    (index: number) => {
      if (editIndex !== null) return false;
      if (index < 0 || index >= completed.length) return false;
      if (!sectionWorkHistoryDone) return false;
      if (inMemberFlow) return false;
      const isPendingCollapsed = pendingGateEntryIndex === index && !entryFieldsExpanded;
      return !isPendingCollapsed;
    },
    [editIndex, completed.length, sectionWorkHistoryDone, inMemberFlow, pendingGateEntryIndex, entryFieldsExpanded]
  );

  const undoLabel = t('submitDocuments.chat.undo');
  const editLabel = t('submitDocuments.fillFormsChecklist.edit');
  const showUndoForStep = useCallback(
    (s: CareerHistorySurveyStep) => {
      if (showFieldEdit || editingFromReview || showPendingSummaryUndo) return false;
      if (prevStepForUndo(step, draft) === s) return true;
      if (s === 'siteName' && step === 'siteName' && newEntryAfterRecordGateYes) return true;
      return false;
    },
    [showFieldEdit, editingFromReview, showPendingSummaryUndo, step, draft, newEntryAfterRecordGateYes]
  );

  const userAnswerForStep = useCallback(
    (s: CareerHistorySurveyStep) => {
      if (s === 'recordGate') {
        if (recordGateAnswer === 'yes') return t('submitDocuments.careerHistorySurvey.yes');
        if (recordGateAnswer === 'no') return t('submitDocuments.careerHistorySurvey.no');
        return '';
      }
      if (isPneuStep(s)) return userAnswerForPneuStep(s, pneuDraft, t, locale);
      if (isEntryStep(s)) return userAnswerForEntryStep(s, draft, t, locale);
      return '';
    },
    [recordGateAnswer, pneuDraft, draft, t, locale]
  );

  const showUndoForPneuStep = useCallback(
    (s: PneuCheckupStep) => {
      if (!pneuUndoActive) return false;
      const undoFrom = step === PNEU_SECTION_COMPLETE_STEP ? PNEU_SECTION_COMPLETE_STEP : step;
      return prevPneuStepForUndo(undoFrom as PneuCheckupStep, pneuDraft) === s;
    },
    [pneuUndoActive, step, pneuDraft]
  );

  const handlePneuGate = useCallback(
    async (v: YesNo) => {
      const nextDraft = { ...pneuDraft, gateAnswer: v };
      setPneuDraft(nextDraft);
      setPneuGateCommitted(true);
      markPneuCommitted(PNEU_GATE_STEP);
      const next = nextPneuStepAfter(PNEU_GATE_STEP, v);
      if (!next) return;
      const sectionDone = next === PNEU_SECTION_COMPLETE_STEP;
      if (sectionDone) setSectionPneuDone(true);
      setStep(next);
      await persistPneu(nextDraft, next, sectionDone);
    },
    [pneuDraft, markPneuCommitted, persistPneu]
  );

  const submitPneuText = useCallback(
    async (slot: 1 | 2, field: 'contractor' | 'area', from: PneuCheckupStep, value: string) => {
      const nextDraft = {
        ...pneuDraft,
        ...(slot === 1
          ? { slot1: { ...pneuDraft.slot1, [field]: value } }
          : { slot2: { ...pneuDraft.slot2, [field]: value } }),
      };
      setPneuDraft(nextDraft);
      await advancePneuFrom(from, nextDraft);
    },
    [pneuDraft, advancePneuFrom]
  );

  const handlePneuFindings = useCallback(
    async (slot: 1 | 2, from: PneuCheckupStep, v: YesNo) => {
      const nextDraft = {
        ...pneuDraft,
        ...(slot === 1
          ? { slot1: { ...pneuDraft.slot1, findings: v } }
          : { slot2: { ...pneuDraft.slot2, findings: v } }),
      };
      setPneuDraft(nextDraft);
      await advancePneuFrom(from, nextDraft);
    },
    [pneuDraft, advancePneuFrom]
  );

  const handlePneu1Date = useCallback(async () => {
    const iso = isoFromWheel(pneu1DateWheel);
    const nextDraft = { ...pneuDraft, slot1: { ...pneuDraft.slot1, checkupDate: iso } };
    setPneuDraft(nextDraft);
    await advancePneuFrom('pneu1Date', nextDraft);
  }, [pneu1DateWheel, pneuDraft, advancePneuFrom]);

  const handlePneu2Date = useCallback(async () => {
    const iso = isoFromWheel(pneu2DateWheel);
    const nextDraft = { ...pneuDraft, slot2: { ...pneuDraft.slot2, checkupDate: iso, }, slot2Skipped: false };
    setPneuDraft(nextDraft);
    await advancePneuFrom('pneu2Date', nextDraft);
  }, [pneu2DateWheel, pneuDraft, advancePneuFrom]);

  const skipPneu2 = useCallback(async () => {
    const nextDraft = { ...pneuDraft, slot2Skipped: true, slot2: emptyPneuSlotDraft() };
    setPneuDraft(nextDraft);
    markPneuCommitted('pneu2Date');
    markPneuCommitted('pneu2Contractor');
    markPneuCommitted('pneu2Findings');
    markPneuCommitted('pneu2Area');
    setSectionPneuDone(true);
    setStep(PNEU_SECTION_COMPLETE_STEP);
    await persistPneu(nextDraft, PNEU_SECTION_COMPLETE_STEP, true);
  }, [pneuDraft, markPneuCommitted, persistPneu]);

  const handleUndoPneuGate = useCallback(async () => {
    setPneuGateCommitted(false);
    const reset = emptyPneuCheckupDraft();
    setPneuDraft(reset);
    setPneuCommitted(new Set());
    setSectionPneuDone(false);
    setStep(PNEU_GATE_STEP);
    await persistPneu(reset, PNEU_GATE_STEP, false);
  }, [persistPneu]);

  const handleUndoPneuStep = useCallback(
    async (undoFrom: PneuCheckupStep) => {
      if (undoFrom === 'pneu1Date') {
        await handleUndoPneuGate();
        return;
      }
      const clearedDraft = clearPneuDraftFromStep(pneuDraft, undoFrom);
      if (PNEU_FIELD_STEPS.indexOf(undoFrom) < 0) return;
      setPneuDraft(clearedDraft);
      setPneuCommitted(committedPneuStepsForDraft(clearedDraft));
      setSectionPneuDone(false);
      setStep(undoFrom);
      await persistPneu(clearedDraft, undoFrom, false);
    },
    [handleUndoPneuGate, pneuDraft, persistPneu]
  );

  const handleFinalComplete = useCallback(async (): Promise<boolean> => {
    setSaveError(null);
    return true;
  }, []);

  return {
    t,
    step,
    draft,
    setDraft,
    isLoading,
    saveError,
    isComplete: sectionPneuDone && step === PNEU_SECTION_COMPLETE_STEP && editIndex === null,
    threadEndRef,
    currentQuestionRef,
    handleFinalComplete,
    skipIntro,
    handleRecordGate,
    submitTextField,
    handleStartDate,
    handleEndDate,
    skipEndDate,
    confirmEntrySummary,
    handleUndoStep,
    handleUndoPendingSummary,
    handleUndoRecordGateNo,
    handleEditSummary,
    pendingGateEntryIndex,
    entryFieldsExpanded,
    showPendingSummaryUndo,
    showUndoRecordGateNo,
    pendingReEdit,
    beginEditField,
    collapseEditReview,
    showFieldEdit,
    showEditOnSummary,
    isCommitted,
    committed,
    completed,
    recordGateAnswer,
    recordGateCommitted,
    editIndex,
    editingFromReview,
    reviewMode,
    sectionWorkHistoryDone,
    sectionPneuDone,
    inPneuFlow,
    inMemberFlow,
    pneuDraft,
    setPneuDraft,
    pneuGateCommitted,
    showUndoPneuGate,
    handlePneuGate,
    handlePneuFindings,
    submitPneuText,
    handlePneu1Date,
    handlePneu2Date,
    skipPneu2,
    handleUndoPneuGate,
    handleUndoPneuStep,
    showUndoForPneuStep,
    isPneuCommitted,
    pneu1DateWheel,
    setPneu1DateWheel,
    pneu2DateWheel,
    setPneu2DateWheel,
    startDateWheel,
    setStartDateWheel,
    endDateWheel,
    setEndDateWheel,
    stepQuestionKey,
    userAnswerForStep,
    showUndoForStep,
    undoLabel,
    editLabel,
    entrySteps: ENTRY_STEPS,
  };
}

export type CareerHistorySurveyFlow = ReturnType<typeof useCareerHistorySurveyFlow>;
